import { useLayoutEffect, useRef } from 'react';
import { Outlet, useLocation, useMatches, useNavigationType } from 'react-router-dom';
import { useTenant } from '../lib/tenant-context';

/** Orient once per address, including headings that arrive in lazy chunks or query results. */
export function RouteAccess() {
  const { pathname, hash } = useLocation();
  const navigation = useNavigationType();
  const matches = useMatches();
  const {
    title = 'Page not found',
    detail = false,
    preserveScroll = false,
  } = (matches.at(-1)?.handle as
    { title?: string; detail?: boolean; preserveScroll?: boolean } | undefined) ?? {};
  const tenant = useTenant();
  const content = useRef<HTMLDivElement>(null);
  const announcement = useRef<HTMLParagraphElement>(null);
  const orientation = useRef({ pathname: '', done: false, interacted: false });

  useLayoutEffect(() => {
    const root = content.current;
    const status = announcement.current;
    if (!root || !status) return;
    if (orientation.current.pathname !== pathname)
      orientation.current = { pathname, done: false, interacted: false };
    const state = orientation.current;
    const preserveFocus = () => {
      state.interacted = true;
    };
    const update = () => {
      const main = root.querySelector('main');
      const heading = Array.from(main?.querySelectorAll('h1') ?? []).find(
        (node) => node.getClientRects().length > 0,
      );
      const headingText = heading?.textContent?.replace(/\s+/g, ' ').trim();
      // A heading can name its page for the title differently from its own words, as the certificate
      // does with its code, so the tab never reads "Certificate: Certificate of completion" (L-52).
      const detailText = heading?.dataset.routeDetail?.trim() || headingText;
      const brand = main?.dataset.academyName ?? tenant.name;
      const pageTitle = `${title}${detail && detailText ? `: ${detailText}` : ''} · ${brand}`;
      if (document.title !== pageTitle) document.title = pageTitle;
      if (state.done) return;
      if (!heading) {
        if (status.textContent !== pageTitle) status.textContent = pageTitle;
        return;
      }
      state.done = true;
      if (state.interacted) status.textContent = headingText ?? title;
      else {
        status.textContent = '';
        heading.tabIndex = -1;
        heading.focus({ preventScroll: preserveScroll || navigation === 'POP' || !!hash });
      }
    };
    // Query components can finish without rerendering the router. Observe only this document's
    // route content; the brand preview's iframe and routine attribute changes cannot take focus.
    const observer = new MutationObserver(update);
    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['data-academy-name'],
    });
    document.addEventListener('keydown', preserveFocus, true);
    document.addEventListener('pointerdown', preserveFocus, true);
    document.addEventListener('focusin', preserveFocus, true);
    update();
    return () => {
      observer.disconnect();
      document.removeEventListener('keydown', preserveFocus, true);
      document.removeEventListener('pointerdown', preserveFocus, true);
      document.removeEventListener('focusin', preserveFocus, true);
    };
  }, [pathname, hash, navigation, title, detail, preserveScroll, tenant.name]);

  return (
    <>
      <a
        href="#main"
        className="skip-link"
        onClick={(event) => {
          const main = content.current?.querySelector('main');
          if (main) {
            event.preventDefault();
            main.focus();
          }
        }}
      >
        Skip to main content
      </a>
      <p ref={announcement} className="route-announcement sr-only" aria-live="polite" aria-atomic="true" />
      <div ref={content}>
        <Outlet />
      </div>
    </>
  );
}
